/**
 * Section rendering utilities for cart updates
 * @module shared-cart/cart/sections
 */

/**
 * Self-contained inline spinner that does not depend on theme CSS classes.
 * Embeds its own keyframe animation so it works in any container.
 * @constant {string}
 */
export const CART_SPINNER_HTML = `<span style="display:inline-block;width:10px;height:10px;vertical-align:middle;position:relative;left:-1px;top:1px;"><svg width="10" height="10" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" style="animation:shared-cart-spin 0.55s linear infinite;"><circle fill="none" stroke-width="10" cx="33" cy="33" r="28" stroke="currentColor" stroke-dasharray="110" stroke-dashoffset="55" stroke-linecap="round"></circle></svg><style>@keyframes shared-cart-spin{to{transform:rotate(360deg)}}</style></span>`;

interface SectionConfig {
  id: string;
  section: string;
  selector: string;
}

interface CartFetchConfig {
  method: 'POST';
  headers: {
    'Content-Type': string;
    Accept: string;
  };
}

export interface CartSectionState {
  items: unknown[];
  sections?: Record<string, string | undefined>;
  item_count?: number | null;
}

/**
 * Creates the fetch configuration for cart API requests
 * @returns {Object} Fetch config with method and headers
 */
export function fetchConfig(): CartFetchConfig {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };
}

/**
 * Gets the sections URL with pagination parameter if present
 * @returns {string} The sections URL
 */
export function getSectionsUrl(): string {
  const pageParams = new URLSearchParams(window.location.search);
  const pageNumber = decodeURIComponent(pageParams.get('page') || '');
  return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
}

/**
 * @typedef {Object} SectionConfig
 * @property {string} id - The element ID
 * @property {string} section - The section name for Shopify
 * @property {string} selector - CSS selector for inner content
 */

/**
 * Returns the list of sections to render after cart updates
 * @returns {SectionConfig[]} Array of section configurations
 */
export function getSectionsToRender(): SectionConfig[] {
  return [
    {
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '#shopify-section-cart-icon-bubble',
    },
    {
      id: 'CartDrawer',
      section: 'cart-drawer',
      selector: '.drawer__inner',
    },
  ];
}

/**
 * Extracts inner HTML from a section's HTML string
 * @param {string} html - The full HTML string
 * @param {string} selector - CSS selector for the element to extract
 * @returns {string} The inner HTML of the selected element
 */
export function getSectionInnerHTML(html: string, selector: string): string {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)!.innerHTML;
}

/**
 * Renders updated sections in the DOM after cart changes
 * @param {Object} parsedState - The cart state with sections data
 */
export function renderSections(parsedState: CartSectionState): void {
  getSectionsToRender().forEach((section) => {
    const sectionElement = document.getElementById(section.id);

    if (section.section === 'cart-drawer') {
      sectionElement
        ?.closest('cart-drawer')
        ?.classList.toggle('is-empty', parsedState.items.length === 0);
    }

    const elementToReplace = sectionElement?.querySelector(section.selector) || sectionElement;

    if (elementToReplace && parsedState.sections?.[section.section]) {
      elementToReplace.innerHTML = getSectionInnerHTML(
        parsedState.sections![section.section]!,
        section.selector,
      );
    }
  });

  // Update cart quantity display after innerHTML replacement recreates the span
  if (parsedState.item_count !== null && parsedState.item_count !== undefined) {
    const quantityEl = document.querySelector('.cart-total-quantity');
    if (quantityEl) {
      quantityEl.textContent =
        parsedState.item_count === 1 ? '1 Item' : `${parsedState.item_count} Items`;
    }
  }
}

/**
 * Renders a full-drawer "Restoring your cart..." overlay inside `#CartDrawer
 * .drawer__inner`. Used during multi-step cart restorations (backup-on-back-nav,
 * warehouse-cart preservation) where the customer should see the drawer is
 * being repopulated rather than briefly empty. Cleared when `renderSections()`
 * replaces the drawer markup after the restore completes.
 */
export function showRestoringState(): void {
  const drawerInner = document.querySelector<HTMLElement>('#CartDrawer .drawer__inner');
  if (!drawerInner) return;

  const cartDrawer = drawerInner.closest('cart-drawer');
  if (cartDrawer) {
    cartDrawer.classList.remove('is-empty');
  }

  drawerInner.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:200px;flex-direction:column;gap:16px;">
      <p style="margin:0;">Restoring your cart...</p>
      ${CART_SPINNER_HTML}
    </div>
  `;
}

/**
 * Replaces the cart count and drawer quantity text with a loading spinner.
 * Automatically cleaned up when renderSections() replaces the section HTML.
 */
export function showCartSpinner(): void {
  // Skip if already showing a spinner
  if (document.querySelector('#cart-icon-bubble .cart-count-bubble svg')) return;

  // Header cart count bubble
  const countBubble = document.querySelector<HTMLElement>('#cart-icon-bubble .cart-count-bubble');
  if (countBubble) {
    countBubble.innerHTML = CART_SPINNER_HTML;
  }

  // Cart drawer heading quantity
  const quantityEl = document.querySelector<HTMLElement>('.cart-total-quantity');
  if (quantityEl) {
    quantityEl.innerHTML = CART_SPINNER_HTML;
  }
}
